import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ForwardedRef,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
  useMonkeysLocaleMessages,
} from '../../provider/context';
import type {
  BaseLayoutPaneProps,
  BaseLayoutProps,
  BaseLayoutResizeHandleProps,
  BaseLayoutSplitProps,
  BaseLayoutOrientation,
} from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

const DEFAULT_SPLIT_SIZE = 50;
const DEFAULT_MIN_SIZE = 20;
const DEFAULT_MAX_SIZE = 80;
const DEFAULT_KEYBOARD_STEP = 2.5;

type CssVariableStyle = CSSProperties & Record<`--${string}`, string>;

function assignRef<T>(ref: ForwardedRef<T>, value: T | null) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
}

function assertBoolean(name: string, value: boolean) {
  if (typeof value !== 'boolean') {
    throw new TypeError(`BaseLayout ${name} must be a boolean.`);
  }
}

function assertOrientation(orientation: BaseLayoutOrientation) {
  if (orientation !== 'horizontal' && orientation !== 'vertical') {
    throw new TypeError(
      'BaseLayout orientation must be "horizontal" or "vertical".',
    );
  }
}

function toCssSize(value: number | string | undefined, fallback: string) {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0) {
      throw new Error('BaseLayout size and gap props must be finite, non-negative numbers.');
    }
    return `${value}px`;
  }
  if (typeof value === 'string') {
    if (value.trim().length === 0) {
      throw new TypeError('BaseLayout size and gap string props must not be empty.');
    }
    return value;
  }
  return fallback;
}

function assertPercent(name: string, value: number) {
  if (!Number.isFinite(value)) {
    throw new Error(`BaseLayoutSplit ${name} must be a finite number.`);
  }
  if (value < 0 || value > 100) {
    throw new Error(`BaseLayoutSplit ${name} must be between 0 and 100.`);
  }
  return value;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function resolveSplitBounds({
  minSize = DEFAULT_MIN_SIZE,
  maxSize = DEFAULT_MAX_SIZE,
  keyboardStep = DEFAULT_KEYBOARD_STEP,
}: Pick<BaseLayoutSplitProps, 'minSize' | 'maxSize' | 'keyboardStep'>) {
  const min = assertPercent('minSize', minSize);
  const max = assertPercent('maxSize', maxSize);
  if (min > max) {
    throw new Error('BaseLayoutSplit minSize cannot be greater than maxSize.');
  }
  if (!Number.isFinite(keyboardStep) || keyboardStep <= 0) {
    throw new Error('BaseLayoutSplit keyboardStep must be a finite positive number.');
  }
  return { min, max, keyboardStep };
}

function resolveSplitSize(
  name: string,
  value: number | undefined,
  fallback: number,
  min: number,
  max: number,
) {
  return clamp(assertPercent(name, value ?? fallback), min, max);
}

function getSeparatorOrientation(orientation: BaseLayoutOrientation) {
  return orientation === 'horizontal' ? 'vertical' : 'horizontal';
}

function isRtl(element: HTMLElement) {
  const ownerWindow = element.ownerDocument.defaultView;
  if (ownerWindow) {
    return ownerWindow.getComputedStyle(element).direction === 'rtl';
  }
  return element.closest('[dir="rtl"]') !== null;
}

function setPointerCaptureIfAvailable(target: HTMLElement, pointerId: number) {
  if (!target.setPointerCapture) return;
  try {
    target.setPointerCapture(pointerId);
  } catch (error) {
    const OwnerDOMException = target.ownerDocument.defaultView?.DOMException;
    if (
      OwnerDOMException &&
      error instanceof OwnerDOMException &&
      error.name === 'NotFoundError'
    ) {
      // Synthetic pointer events may not register an active platform pointer.
      // Document listeners below preserve the drag contract in that environment.
      return;
    }
    throw error;
  }
}

export const BaseLayout = forwardRef<HTMLDivElement, BaseLayoutProps>(
  function BaseLayout(
    {
      header,
      sidebar,
      aside,
      sidebarLabel,
      asideLabel,
      footer,
      children,
      sidebarSize,
      asideSize,
      gap,
      fullHeight = false,
      appearance,
      className,
      classNames,
      style,
      ...layoutProps
    },
    ref,
  ) {
    assertBoolean('fullHeight', fullHeight);
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const hasHeader = hasRenderableNode(header);
    const hasSidebar = hasRenderableNode(sidebar);
    const hasAside = hasRenderableNode(aside);
    const hasFooter = hasRenderableNode(footer);
    const bodyColumns = [
      hasSidebar ? 'var(--base-layout-sidebar-size)' : undefined,
      'minmax(0, 1fr)',
      hasAside ? 'var(--base-layout-aside-size)' : undefined,
    ]
      .filter(Boolean)
      .join(' ');
    const layoutRows = [
      hasHeader ? 'auto' : undefined,
      'minmax(0, 1fr)',
      hasFooter ? 'auto' : undefined,
    ]
      .filter(Boolean)
      .join(' ');
    const resolvedGap = gap !== undefined ? toCssSize(gap, '0px') : undefined;

    const layoutStyle: CssVariableStyle = {
      ...theme.styles.layout,
      '--base-layout-sidebar-size': toCssSize(sidebarSize, '16rem'),
      '--base-layout-aside-size': toCssSize(asideSize, '20rem'),
      gridTemplateRows: layoutRows,
      ...(resolvedGap !== undefined ? { gap: resolvedGap } : {}),
      ...style,
    };
    const bodyStyle: CSSProperties = {
      ...theme.styles.layoutBody,
      gridTemplateColumns: bodyColumns,
      ...(resolvedGap !== undefined ? { gap: resolvedGap } : {}),
    };

    return (
      <div
        {...componentAttributes}
        {...layoutProps}
        ref={ref}
        data-monkeys-component="layout"
        data-slot="root"
        data-full-height={fullHeight ? 'true' : 'false'}
        className={cn(
          theme.slots.layoutRoot,
          fullHeight && theme.slots.layoutRootFull,
          classNames?.root,
          className,
        )}
        style={layoutStyle}
      >
        {hasHeader ? (
          <div
            data-slot="header"
            className={cn(theme.slots.layoutHeader, classNames?.header)}
            style={theme.styles.layoutHeader}
          >
            {header}
          </div>
        ) : null}

        <div
          data-slot="body"
          className={cn(theme.slots.layoutBody, classNames?.body)}
          style={bodyStyle}
        >
          {hasSidebar ? (
            <aside
              aria-label={sidebarLabel}
              data-slot="sidebar"
              className={cn(theme.slots.layoutSidebar, classNames?.sidebar)}
              style={theme.styles.layoutSidebar}
            >
              {sidebar}
            </aside>
          ) : null}

          <main
            data-slot="main"
            className={cn(theme.slots.layoutMain, classNames?.main)}
            style={theme.styles.layoutMain}
          >
            {children}
          </main>

          {hasAside ? (
            <aside
              aria-label={asideLabel}
              data-slot="aside"
              className={cn(theme.slots.layoutAside, classNames?.aside)}
              style={theme.styles.layoutAside}
            >
              {aside}
            </aside>
          ) : null}
        </div>

        {hasFooter ? (
          <div
            data-slot="footer"
            className={cn(theme.slots.layoutFooter, classNames?.footer)}
            style={theme.styles.layoutFooter}
          >
            {footer}
          </div>
        ) : null}
      </div>
    );
  },
);

export const BaseLayoutPane = forwardRef<HTMLElement, BaseLayoutPaneProps>(
  function BaseLayoutPane(
    {
      as = 'div',
      scrollable = false,
      bordered = true,
      surface = 'default',
      children,
      appearance,
      className,
      classNames,
      style,
      ...paneProps
    },
    ref,
  ) {
    assertBoolean('scrollable', scrollable);
    assertBoolean('bordered', bordered);
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const Component = as as ElementType;

    return (
      <Component
        {...componentAttributes}
        {...paneProps}
        ref={ref}
        data-monkeys-component="layout-pane"
        data-slot="root"
        data-scrollable={scrollable ? 'true' : 'false'}
        data-surface={surface}
        className={cn(
          theme.slots.layoutPane,
          scrollable && theme.slots.layoutPaneScrollable,
          bordered && theme.slots.layoutPaneBordered,
          surface === 'muted' && theme.slots.layoutPaneMuted,
          surface === 'soft' && theme.slots.layoutPaneSoft,
          surface === 'transparent' && theme.slots.layoutPaneTransparent,
          classNames?.root,
          className,
        )}
        style={{
          ...theme.styles.layoutPane,
          ...style,
        }}
      >
        {children}
      </Component>
    );
  },
);

export const BaseLayoutResizeHandle = forwardRef<
  HTMLDivElement,
  BaseLayoutResizeHandleProps
>(function BaseLayoutResizeHandle(
  {
    orientation = 'horizontal',
    resizable = false,
    disabled = false,
    size,
    minSize,
    maxSize,
    label,
    appearance,
    className,
    classNames,
    style,
    tabIndex,
    ...handleProps
  },
  ref,
) {
  assertOrientation(orientation);
  assertBoolean('resizable', resizable);
  assertBoolean('disabled', disabled);
  const resolvedMin = minSize === undefined ? undefined : assertPercent('minSize', minSize);
  const resolvedMax = maxSize === undefined ? undefined : assertPercent('maxSize', maxSize);
  if (
    resolvedMin !== undefined &&
    resolvedMax !== undefined &&
    resolvedMin > resolvedMax
  ) {
    throw new Error('BaseLayoutSplit minSize cannot be greater than maxSize.');
  }
  const resolvedSize =
    size === undefined
      ? undefined
      : clamp(
          assertPercent('size', size),
          resolvedMin ?? 0,
          resolvedMax ?? 100,
        );
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const messages = useMonkeysLocaleMessages().layout;
  const theme = resolveBaseAppearance(resolvedAppearance);
  const resolvedLabel = label ?? messages.resizePanels;
  if (
    resizable &&
    (typeof resolvedLabel !== 'string' || resolvedLabel.trim().length === 0)
  ) {
    throw new TypeError('BaseLayoutResizeHandle label must be a non-empty string.');
  }
  const interactive = resizable && !disabled;
  const separatorOrientation = getSeparatorOrientation(orientation);

  return (
    <div
      {...componentAttributes}
      {...handleProps}
      ref={ref}
      data-monkeys-component="layout-resize-handle"
      data-slot="handle"
      data-orientation={orientation}
      data-state={interactive ? 'enabled' : disabled ? 'disabled' : 'static'}
      role={resizable ? 'separator' : undefined}
      aria-label={resizable ? resolvedLabel : undefined}
      aria-orientation={resizable ? separatorOrientation : undefined}
      aria-valuemin={resizable ? resolvedMin : undefined}
      aria-valuemax={resizable ? resolvedMax : undefined}
      aria-valuenow={resizable ? resolvedSize : undefined}
      aria-disabled={resizable && disabled ? true : undefined}
      tabIndex={interactive ? tabIndex ?? 0 : undefined}
      className={cn(
        theme.slots.layoutResizeHandle,
        orientation === 'horizontal'
          ? theme.slots.layoutResizeHandleHorizontal
          : theme.slots.layoutResizeHandleVertical,
        !interactive && theme.slots.layoutResizeHandleDisabled,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.layoutResizeHandle,
        ...style,
      }}
    >
      <span
        aria-hidden="true"
        data-slot="indicator"
        className={cn(
          theme.slots.layoutResizeHandleIndicator,
          orientation === 'horizontal' ? 'h-8 w-px' : 'h-px w-8',
          classNames?.indicator,
        )}
      />
    </div>
  );
});

export const BaseLayoutSplit = forwardRef<HTMLDivElement, BaseLayoutSplitProps>(
  function BaseLayoutSplit(
    {
      orientation = 'horizontal',
      primary,
      secondary,
      children,
      resizable = false,
      disabled = false,
      size,
      defaultSize,
      minSize,
      maxSize,
      gap,
      keyboardStep,
      resizeLabel,
      appearance,
      className,
      classNames,
      style,
      onSizeChange,
      ...splitProps
    },
    ref,
  ) {
    assertOrientation(orientation);
    assertBoolean('resizable', resizable);
    assertBoolean('disabled', disabled);
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const bounds = resolveSplitBounds({ minSize, maxSize, keyboardStep });
    const initialSize = resolveSplitSize(
      'defaultSize',
      defaultSize,
      DEFAULT_SPLIT_SIZE,
      bounds.min,
      bounds.max,
    );
    const [uncontrolledSize, setUncontrolledSize] = useState(initialSize);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const cleanupRef = useRef<(() => void) | null>(null);
    const controlled = size !== undefined;
    const interactive = resizable && !disabled;
    const renderedSize = resolveSplitSize(
      'size',
      controlled ? size : uncontrolledSize,
      initialSize,
      bounds.min,
      bounds.max,
    );
    const resolvedDirection = splitProps.dir ?? componentAttributes.dir;

    const setRootRef = useCallback(
      (node: HTMLDivElement | null) => {
        rootRef.current = node;
        assignRef(ref, node);
      },
      [ref],
    );

    const commitSize = useCallback(
      (nextSize: number) => {
        const clampedSize = clamp(nextSize, bounds.min, bounds.max);
        if (!controlled) {
          setUncontrolledSize(clampedSize);
        }
        onSizeChange?.(clampedSize);
      },
      [bounds.max, bounds.min, controlled, onSizeChange],
    );

    const updateFromPointer = useCallback(
      (event: Pick<PointerEvent, 'clientX' | 'clientY'>) => {
        const root = rootRef.current;
        if (!root) return;
        const rect = root.getBoundingClientRect();
        const total = orientation === 'horizontal' ? rect.width : rect.height;
        if (!Number.isFinite(total) || total <= 0) return;
        const offset =
          orientation === 'horizontal'
            ? isRtl(root)
              ? rect.right - event.clientX
              : event.clientX - rect.left
            : event.clientY - rect.top;
        commitSize((offset / total) * 100);
      },
      [commitSize, orientation],
    );

    const stopResize = useCallback(() => {
      const cleanup = cleanupRef.current;
      cleanupRef.current = null;
      cleanup?.();
    }, []);

    useEffect(() => {
      if (!interactive) stopResize();
      return stopResize;
    }, [interactive, stopResize]);

    const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!interactive || (event.button !== 0 && event.pointerType !== 'touch')) {
        return;
      }
      event.preventDefault();
      stopResize();
      updateFromPointer(event);

      const pointerId = event.pointerId;
      const target = event.currentTarget;
      const ownerDocument = target.ownerDocument;

      const handleMove = (nextEvent: PointerEvent) => {
        if (nextEvent.pointerId !== pointerId) return;
        updateFromPointer(nextEvent);
      };
      const handleStop = (nextEvent: PointerEvent) => {
        if (nextEvent.pointerId !== pointerId) return;
        stopResize();
      };
      const handleLostCapture = (nextEvent: PointerEvent) => {
        if (nextEvent.pointerId !== pointerId) return;
        stopResize();
      };

      ownerDocument.addEventListener('pointermove', handleMove);
      ownerDocument.addEventListener('pointerup', handleStop);
      ownerDocument.addEventListener('pointercancel', handleStop);
      target.addEventListener('lostpointercapture', handleLostCapture);
      cleanupRef.current = () => {
        ownerDocument.removeEventListener('pointermove', handleMove);
        ownerDocument.removeEventListener('pointerup', handleStop);
        ownerDocument.removeEventListener('pointercancel', handleStop);
        target.removeEventListener('lostpointercapture', handleLostCapture);
        if (target.hasPointerCapture?.(pointerId)) {
          target.releasePointerCapture?.(pointerId);
        }
      };
      setPointerCaptureIfAvailable(target, pointerId);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (!interactive) return;
      const step = event.shiftKey ? bounds.keyboardStep * 4 : bounds.keyboardStep;
      const rtl = orientation === 'horizontal' && isRtl(event.currentTarget);
      let nextSize: number | undefined;

      if (event.key === 'Home') nextSize = bounds.min;
      if (event.key === 'End') nextSize = bounds.max;
      if (orientation === 'horizontal' && event.key === 'ArrowLeft') {
        nextSize = renderedSize + (rtl ? step : -step);
      }
      if (orientation === 'horizontal' && event.key === 'ArrowRight') {
        nextSize = renderedSize + (rtl ? -step : step);
      }
      if (orientation === 'vertical' && event.key === 'ArrowUp') {
        nextSize = renderedSize - step;
      }
      if (orientation === 'vertical' && event.key === 'ArrowDown') {
        nextSize = renderedSize + step;
      }

      if (nextSize === undefined) return;
      event.preventDefault();
      commitSize(nextSize);
    };

    const resolvedGap = gap !== undefined ? toCssSize(gap, '0px') : undefined;
    const resizeTrack =
      resolvedGap !== undefined ? 'var(--base-layout-split-gap)' : 'auto';
    const resizeHandleStyle: CSSProperties | undefined =
      resolvedGap !== undefined
        ? orientation === 'horizontal'
          ? { width: 'var(--base-layout-split-gap)' }
          : { height: 'var(--base-layout-split-gap)' }
        : undefined;
    const splitStyle: CssVariableStyle = {
      ...theme.styles.layoutSplit,
      [orientation === 'horizontal' ? 'gridTemplateColumns' : 'gridTemplateRows']:
        resizable
          ? `${renderedSize}% ${resizeTrack} minmax(0, 1fr)`
          : `${renderedSize}% minmax(0, 1fr)`,
      ...(resizable ? { gap: 0 } : {}),
      ...(resolvedGap !== undefined
        ? {
            '--base-layout-split-gap': resolvedGap,
            ...(resizable ? {} : { gap: resolvedGap }),
          }
        : {}),
      ...style,
    };

    return (
      <div
        {...componentAttributes}
        {...splitProps}
        ref={setRootRef}
        data-monkeys-component="layout-split"
        data-slot="root"
        data-orientation={orientation}
        data-state={interactive ? 'enabled' : disabled ? 'disabled' : 'static'}
        data-controlled={controlled ? 'true' : 'false'}
        className={cn(
          theme.slots.layoutSplitRoot,
          orientation === 'horizontal'
            ? theme.slots.layoutSplitHorizontal
            : theme.slots.layoutSplitVertical,
          classNames?.root,
          className,
        )}
        style={splitStyle}
      >
        <div
          data-slot="primary"
          className={cn(
            theme.slots.layoutSplitPane,
            theme.slots.layoutSplitPrimary,
            classNames?.primary,
          )}
          style={theme.styles.layoutSplitPane}
        >
          {hasRenderableNode(primary) ? primary : children}
        </div>

        {resizable ? (
          <BaseLayoutResizeHandle
            orientation={orientation}
            resizable
            disabled={disabled}
            size={renderedSize}
            minSize={bounds.min}
            maxSize={bounds.max}
            label={resizeLabel}
            appearance={appearance}
            dir={resolvedDirection}
            className={classNames?.handle}
            style={resizeHandleStyle}
            onPointerDown={handlePointerDown}
            onKeyDown={handleKeyDown}
          />
        ) : null}

        <div
          data-slot="secondary"
          className={cn(
            theme.slots.layoutSplitPane,
            theme.slots.layoutSplitSecondary,
            classNames?.secondary,
          )}
          style={theme.styles.layoutSplitPane}
        >
          {secondary}
        </div>
      </div>
    );
  },
);
